import { Component, Mixins } from 'vue-property-decorator'
import { email, maxLength, minLength, required, requiredIf } from 'vuelidate/lib/validators'

import { Validation, validationMixin } from 'vuelidate'

import { CardsService } from '@/api'
import { mock } from '@/tools'
import { ClaimForm } from '@/models'

@Component<ConnectCardValidations>({
  validations: {
    form: {
      mainData: {
        middleName: {
          maxLength: maxLength(60),
        },
        lastName: {
          maxLength: maxLength(60),
          required,
        },
        firstName: {
          maxLength: maxLength(60),
          required,
        },
        gender: { required },
        snils: { required, maxLength: maxLength(14) },
        oms: {
          maxLength: maxLength(16),
        },
        birthPlace: { maxLength: maxLength(700), required },
      },
      contacts: {
        regAddress: { maxLength: maxLength(100), required },
        factAddress: { maxLength: maxLength(100) },
        temporaryRegAddress: { maxLength: maxLength(100) },
        email: { email, required, maxLength: maxLength(50) },
        mobilePhone: { minLength: minLength(16), required },
        phone: { minLength: minLength(16) },
      },
      document: {
        series: {
          required,
          maxLength: maxLength(20),
        },
        number: { required, maxLength: maxLength(20), minLength: minLength(6) },
        issuedBy: { required },
        issueDate: { required },
        issueCode: {
          maxLength: maxLength(7),
          minLength: minLength(7),
          required: requiredIf(({ docType }: { docType: number }) => docType === 1),
        },
        docType: { required },
      },
      cardInfo: {
        cardNumber: {
          minLength: minLength(19),
          maxLength: maxLength(23),
          required,
          async isValid(value: string) {
            if (this.form.isEdit) return true
            if (!value) return false
            try {
              const { message } = await CardsService.checkCardNumber(value.replaceAll(' ', ''))
              this.cardNumberError = message
              return !message
            } catch (error) {
              mock(error)
              this.cardNumberError = 'Не удалось проверить номер карты'
              return false
            }
          },
        },
      },
    },
  },
})
export class ConnectCardValidations extends Mixins<Validation>(validationMixin) {
  cardNumberError: string = null
  form!: ClaimForm

  get cardErrors() {
    if (!this.$v.form.cardInfo.cardNumber.$dirty) return []

    const errors: Array<string> = []
    if (this.$v.form.cardInfo.cardNumber.$anyError)
      if (this.cardNumberError) errors.push(this.cardNumberError)
      else errors.push('Номер карты должен содержать от 16 до 19 цифр')
    return errors
  }

  get omsErrors() {
    if (!this.$v.form.mainData.oms.$dirty) return []

    const errors: Array<string> = []
    if (this.$v.form.mainData.oms.$anyError) errors.push('ОМС должен содержать не более 16 цифр')
    return errors
  }

  get birthPlaceErrors() {
    if (!this.$v.form.mainData.birthPlace.$dirty) return []

    const errors: Array<string> = []
    if (this.$v.form.mainData.birthPlace.$anyError) errors.push('Поле обязательно для заполнения')
    return errors
  }

  get docTypeErrors() {
    if (!this.$v.form.document.docType.$dirty) return []

    const errors: Array<string> = []
    if (this.$v.form.document.docType.$anyError) errors.push('Выберите тип документа')
    return errors
  }

  get docSeriesErrors() {
    if (!this.$v.form.document.series.$dirty) return []

    const errors: Array<string> = []
    if (!this.$v.form.document.series.maxLength) errors.push('Серия документа содержать до 20 символов')
    if (this.$v.form.document.series.$anyError) errors.push('Серия документа обязательна для заполнения')
    return errors
  }

  get docNumberErrors() {
    if (!this.$v.form.document.number.$dirty) return []

    const errors: Array<string> = []
    if (this.$v.form.document.number.$anyError) errors.push('Номер документа должен содержать от 6 до 20 цифр')
    return errors
  }

  get docIssueDateErrors() {
    if (!this.$v.form.document.issueDate.$dirty) return []

    const errors: Array<string> = []
    if (this.$v.form.document.issueDate.$anyError) errors.push('Поле обязательно для заполнения')
    return errors
  }

  get docIssueCodeErrors() {
    if (!this.$v.form.document.issueCode.$dirty) return []

    const errors: Array<string> = []
    if (this.$v.form.document.issueCode.$anyError) errors.push('Поле должно содержать 6 цифр')
    return errors
  }

  get docIssuedByErrors() {
    if (!this.$v.form.document.issuedBy.$dirty) return []

    const errors: Array<string> = []
    if (this.$v.form.document.issuedBy.$anyError) errors.push('Поле обязательно для заполнения')
    return errors
  }

  get factAddrErrors() {
    if (!this.$v.form.contacts.factAddress?.$dirty) return []

    const errors: Array<string> = []
    if (this.$v.form.contacts.factAddress?.$anyError) errors.push('Поле обязательно для заполнения')
    return errors
  }

  get regAddrErrors() {
    if (!this.$v.form.contacts.regAddress?.$dirty) return []

    const errors: Array<string> = []
    if (this.$v.form.contacts.regAddress?.$anyError) errors.push('Поле обязательно для заполнения')
    return errors
  }

  get snilsErrors() {
    if (!this.$v.form.mainData.snils?.$dirty) return []

    const errors: Array<string> = []
    if (this.$v.form.mainData.snils.$anyError) errors.push('Поле обязательно для заполнения')
    return errors
  }

  get mobilePhoneErrors() {
    if (!this.$v.form.contacts.mobilePhone?.$dirty) return []

    const errors: Array<string> = []
    if (this.$v.form.contacts.mobilePhone?.$anyError) errors.push('Перепроверьте номер')
    return errors
  }

  get phoneErrors() {
    if (!this.$v.form.contacts.phone?.$dirty) return []

    const errors: Array<string> = []
    if (this.$v.form.contacts.phone?.$anyError) errors.push('Перепроверьте номер')
    return errors
  }

  get mailErrors() {
    if (!this.$v.form.contacts.email?.$dirty) return []

    const errors: Array<string> = []
    if (!this.$v.form.contacts.email.email) errors.push('Неправильный формат почты')
    if (!this.$v.form.contacts.email.maxLength) errors.push('Почта должна содержать до 50 символов')
    return errors
  }

  get lastNameErrors() {
    if (!this.$v.form.mainData.lastName?.$dirty) return []

    const errors: Array<string> = []
    if (this.$v.form.mainData.lastName?.$anyError) errors.push('Некорректно заполнена Фамилия')
    return errors
  }

  get firstNameErrors() {
    if (!this.$v.form.mainData.firstName?.$dirty) return []

    const errors: Array<string> = []
    if (this.$v.form.mainData.firstName?.$anyError) errors.push('Некорректно заполнено Имя')
    return errors
  }

  get middleNameErrors() {
    if (!this.$v.form.mainData.middleName?.$dirty) return []

    const errors: Array<string> = []
    if (this.$v.form.mainData.middleName?.$anyError) errors.push('Некорректно заполнено Отчество')
    return errors
  }

  get genderErrors() {
    if (!this.$v.form.mainData.gender?.$dirty) return []

    const errors: Array<string> = []
    if (this.$v.form.mainData.gender?.$anyError) errors.push('Необходимо выбрать пол')
    return errors
  }

  get birthDateErrors() {
    if (!this.$v.form.contacts.factAddress?.$dirty) return []

    const errors: Array<string> = []
    if (this.$v.form.contacts.factAddress.$anyError) errors.push('Необходимо указать дату рождения')
    return errors
  }
}
